import * as React from 'react';
import { useParams } from 'react-router-dom';
import { CommandResult, fieldValidation, InternalArticleFieldChange, InternalSubscriberArticleChangeView, InternalSubscriberArticleView, rng } from '../../../../amorg-common';
import { createInternalArticleChange, getInternalArticle, setInternalArticleDeleted, updateInternalArticle } from '../../../../api/siteArticlesClientApi';
import { ImportanceAvatar } from '../../../../designSystem/avatar/ImportanceAvatar';
import { Button } from '../../../../designSystem/button/Button';
import { Card } from '../../../../designSystem/Card';
import { ToolBarContainer } from '../../../../designSystem/compositeComponents/ButtonToolBar';
import { ConfirmationButton } from '../../../../designSystem/compositeComponents/ConfirmationButton';
import { EditableText } from '../../../../designSystem/compositeComponents/EditableText';
import { Flex } from '../../../../designSystem/compositeComponents/FlexHelpers';
import { UserSelectorButton } from '../../../../designSystem/compositeComponents/itemSelector/UserSelectorAvatarButton';
import { Divider } from '../../../../designSystem/Divider';
import { FormField } from '../../../../designSystem/formField/FormField';
import { DateInput } from '../../../../designSystem/inputs/DateInput';
import { HtmlEditorViewer } from '../../../../designSystem/inputs/HtmlEditorViewer';
import { HtmlEditorWithAutoSave } from '../../../../designSystem/inputs/HtmlEditorWithAutoSave';
import { Label } from '../../../../designSystem/Label';
import { SingleColumnLayout } from '../../../_layouts/SingleColumnLayout';
import { LoadingPageOverlay } from '../../../../designSystem/LoadingPageOverlay';
import { PageHeader } from '../../../../designSystem/PageHeader';
import { P, Small } from '../../../../designSystem/typography/Body';
import { H2, H3 } from '../../../../designSystem/typography/Heading';
import { clientAccessHelper } from '../../../../utils/clientAccessHelper';
import { toDateString, toDateTimeString } from '../../../../utils/date';
import { ProtectedPageContext } from '../../../_contexts/ProtectedPageContext';
import { MemberPageWrapper } from '../../../_pageWrappers/MemberPageWrapper';
import { ArticleImportanceSelector } from '../../_components/ArticleImportanceSelector';
import { MessageCard } from '../../../../designSystem/MessageCard';
import { ButtonRow } from '../../../../designSystem/button/ButtonRow';
import { FileLinkCommentsButtonRow } from '../../../../designSystem/compositeComponents/FileLinkCommentsButtonRow';

export function InternalArticlePage (): JSX.Element
{
    const [articleTitle, setArticleTitle] = React.useState<string>('');

    return (
        <MemberPageWrapper
            selectedMenuItemKey={'articles'}
            breadCrumbs={[
                { text: 'Lovservice', url: '/articles' },
                { text: 'Andre forpligtelser', url: '/articles/internal' },
                ...(articleTitle ? [{ text: articleTitle, url: '' }] : []),
            ]}>
            <InternalArticlePageContent onArticleTitleChanged={setArticleTitle} />
        </MemberPageWrapper>
    )

}

type InternalArticlePageContentProps = {
    onArticleTitleChanged:(title:string) => void,
}

 function InternalArticlePageContent (props:InternalArticlePageContentProps): JSX.Element
{
    const activeUser = React.useContext(ProtectedPageContext).user;
    const hasAccess = clientAccessHelper.hasAllPermissions(activeUser, ['ViewCustomFormAnswerList']);

    const { internalArticleId = '' } = useParams<{ internalArticleId: string; }>();

    const [loading, setLoading] = React.useState(true);
    const [article, setArticle] = React.useState<InternalSubscriberArticleView | null>(null);
    const [deletionError, setDeletionError] = React.useState('');
    const canEdit = clientAccessHelper.hasAllPermissions(activeUser, ['EditArticles']);

    React.useEffect(() =>
    {
        
        const getData = async () =>
        {
            setLoading(true);
            const art = await getInternalArticle(internalArticleId);
            setArticle(art);
            document.title = `Amorgy - Lovservice: ${art?.title || ''}`;
            setLoading(false);
            
        };
        getData();
    }, [internalArticleId]);

    async function handleCreateNewVersion ()
    {
        setLoading(true);
        const result = await createInternalArticleChange(internalArticleId || '', rng.getRandomId());
        if (result.isSuccess)
        {
            const art = await getInternalArticle(internalArticleId);
            setArticle(art);
        }
        setLoading(false);
    }

    async function handleSetDeleted ()
    {
        if (!article)
            return;

        setLoading(true);
        setDeletionError('');
        try
        {
            const result = await setInternalArticleDeleted(internalArticleId, !article.isDeleted);
            if (!result.isSuccess)
            {
                setDeletionError('Kunne ikke ændre slettestatus. Prøv igen eller kontakt support.');
                return;
            }
            setArticle(await getInternalArticle(internalArticleId));
        }
        catch
        {
            setDeletionError('Kunne ikke ændre slettestatus. Prøv igen eller kontakt support.');
        }
        finally
        {
            setLoading(false);
        }
    }

    async function handleChange (change: InternalArticleFieldChange) : Promise<CommandResult>
    {
        setLoading(true);
        const result = await updateInternalArticle(internalArticleId || '', change);
        if (result.isSuccess)
        {
            const art = await getInternalArticle(internalArticleId);
            setArticle(art);
            document.title = `Amorgy - Lovservice: ${art?.title || ''}`;
        }
        setLoading(false);
        return result;
    }

    React.useEffect(() =>
    {
        props.onArticleTitleChanged(article?.title || '');
    }, [article]);

    if (!hasAccess)
        return <P>Hov! Du har ikke rettigheder til at tilgå denne side.</P>;

    return (
        <>
        <PageHeader
            headerIcon='balance'
            headerText='Lovservice'
            narrow
            buttons={
                <ToolBarContainer>
                    <Button variant="text" icon={'help'} toolTipText='Du kan bruge andre forpligtelser og krav til at dokumentere krav og forpligtelser, som ikke er lovkrav. Eksempelvis interne politikker og procedurer.' />
                </ToolBarContainer>
            }
        />
        <SingleColumnLayout narrow>
            <Flex flexDirection='column' gap={32}>

                <LoadingPageOverlay loading={loading} />

                {article &&
                    <>
                        {deletionError && <MessageCard variant='error'>{deletionError}</MessageCard>}
                        {article.isDeleted && <MessageCard variant='warning'>Denne forpligtelse er slettet.</MessageCard>}

                        {article.isDeleted
                            ? <ReadOnlyArticleCard article={article} />
                            : <ArticleCard article={article} onChange={handleChange} />
                        }

                        <ButtonRow>
                            {canEdit &&
                                <ConfirmationButton
                                    modalButtonVariant={article.isDeleted ? 'filled' : 'destructive'}
                                    requireKey={false}
                                    icon='interface_actions_delete'
                                    title={article.isDeleted ? 'Gendan forpligtelse' : 'Slet forpligtelse'}
                                    content={<P>Er du sikker på, at du vil {article.isDeleted ? 'gendanne' : 'slette'} denne forpligtelse?</P>}
                                    buttonText={article.isDeleted ? 'Gendan forpligtelse' : 'Slet forpligtelse'}
                                    onConfirm={handleSetDeleted}
                                />
                            }

                            {!article.isDeleted &&
                            <ConfirmationButton
                                modalButtonVariant='filled'
                                requireKey={false}
                                icon="interface_actions_copy"
                                title="Opret ny version"
                                content={
                                    <P>Er du sikker på, at du vil oprette en ny version af denne artikel? Den nuværende version vil blive bevaret som en tidligere version, men vil ikke længere kunne redigeres.</P>
                                }
                                buttonText="Opret ny version"
                                onConfirm={handleCreateNewVersion}
                            />
                            }
                        </ButtonRow>

                        {article.previousVersions.length > 0 &&
                            <Flex flexDirection='column' gap={16}>
                                <H2>Tidligere versioner</H2>

                                {article.previousVersions.map(change =>
                                    <PreviousVersionCard key={change.id} change={change} />
                                )}
                            </Flex>
                        }
                    </>
                }

            </Flex>
        </SingleColumnLayout>
        </>
    );
}

type ArticleCardProps = {
    article: InternalSubscriberArticleView,
    onChange: (change: InternalArticleFieldChange) => Promise<CommandResult>;
};
function ReadOnlyArticleCard({ article }: { article: InternalSubscriberArticleView }): JSX.Element
{
    return (
        <Card variant='outlined'>
            <Flex flexDirection='column' gap={16}>
                <H2>{article.title}</H2>
                <FormField label='Beskrivelse' inputComponent={<HtmlEditorViewer content={article.description} />} />
                <FormField label='Liste' inputComponent={<span>Andre forpligtelser og krav</span>} />
                <FormField label='Ansvarlig' inputComponent={
                    <UserSelectorButton selectedUserId={article.ownerUserId || ''} disabled onUserChange={() => { return; }} />
                } />
                <Small>Sidst redigeret: {toDateTimeString(article.lastEdited.date)} af {article.lastEdited.displayName}</Small>
                <Divider />
                <H3>Version {article.version}</H3>
                {article.version > 1 &&
                    <FormField label='Ændringsbeskrivelse' inputComponent={<HtmlEditorViewer content={article.changeDescription} />} />
                }
                <FormField label='Dato' inputComponent={<span>{article.date ? toDateString(article.date) : '-'}</span>} />
                <FormField label='Vurderet betydning' inputComponent={
                    <ImportanceAvatar importance={article.evaluation.importance || undefined} mode='internal-evaluation' />
                } />
                <FormField label='Evaluering' inputComponent={<HtmlEditorViewer content={article.evaluation.text} />} />
                <Small>Sidst redigeret: {toDateTimeString(article.lastVersionEdited.date)} af {article.lastVersionEdited.displayName}</Small>
            </Flex>
        </Card>
    );
}

function ArticleCard ({ article, onChange }: ArticleCardProps): JSX.Element
{
    const [descriptionError, setDescriptionError] = React.useState<string>('');
    const [changeDescriptionError, setChangeDescriptionError] = React.useState<string>('');    
    const [evaluationError, setEvaluationError] = React.useState<string>('');

    async function handleDescriptionSave (value: string):Promise<boolean>
    {
        let error = '';
        if(!fieldValidation.internalArticle.description(value))
        {
            error = 'Skal udfyldes. Maks 5000 tegn.';
        }
        else
        {
            error = (await onChange({ field: 'description', value })).isSuccess
                ? ''
                : 'Der skete en ukendt fejl.';
        }
        setDescriptionError(error);
        return error === '';
    }

    async function handleChangeDescriptionSave (value: string):Promise<boolean>
    {
        let error = ''
        if(!fieldValidation.internalArticleChange.changeDescription(value))
        {
            error = 'Maks 5000 tegn.';
        }
        else
        {
            error = (await onChange({ field: 'changeDescription', value })).isSuccess
                ? ''
                : 'Der skete en ukendt fejl.';
        }
        setChangeDescriptionError(error);
        return error === '';
    }

    async function handleEvaluationSave (value: string):Promise<boolean>
    {
        let error = '';
        if(!fieldValidation.internalArticleChange.evaluationText(value))
        {
            error = 'Skal udfyldes. Maks 5000 tegn.';
        }
        else
        {
            error = (await onChange({ field: 'evaluationText', value })).isSuccess
                ? ''
                : 'Der skete en ukendt fejl.';
        }
        setEvaluationError(error);
        return error === '';
    }

    return (
        <Card variant="outlined">
            <Flex flexDirection='column' gap={32}>
                <Flex flexDirection='column' gap={16}>
                    <EditableText
                        leadingIcon='stacks'
                        styleMode='H2'
                        value={article?.title || ''}
                        ariaLabel='Titel på anden forpligtelse eller krav'
                        placeholder='Titel...'
                        validator={(value) => fieldValidation.internalArticle.title(value)
                            ? { valid: true }
                            : { valid: false, errorMessage: 'Skal udfyldes. Maks 400 tegn.' }
                        }
                        onSave={(value) => onChange({ field: 'title', value })}
                    />

                    <Flex flexDirection='column' gap={2}>
                        <Label>Beskrivelse</Label>
                        <HtmlEditorWithAutoSave
                            id='descriptionEditor'
                            value={article?.description || ''}
                            ariaLabel='Beskrivelse af forpligtelsen eller kravet'
                            placeholder='Beskrivelse af forpligtelsen eller kravet...'
                            errorMessage={descriptionError}
                            onCancel={() => setDescriptionError('')}
                            onSave={handleDescriptionSave}
                        />
                    </Flex>

                    <Flex gap={64}>
                        <FormField
                            label='Liste'
                            inputComponent={<span>Andre forpligtelser og krav</span>}
                        />

                        <FormField
                            label='Ansvarlig'
                            inputComponent={
                                <UserSelectorButton
                                    selectedUserId={article.ownerUserId || ''}
                                    userFilter={u => u.enabled}
                                    onUserChange={user => onChange({ field: 'ownerUserId', value: user?.id || '' })}
                                />
                            }
                        />
                    </Flex>

                    <Flex justifyContent='flex-end'>
                        <Small>Sidst redigeret: {toDateTimeString(article.lastEdited.date)} af {article?.lastEdited.displayName}</Small>
                    </Flex>

    
                    <Card variant="none" padding="16px 16px 0px 16px">
                        <FileLinkCommentsButtonRow
                            fileReference={{ type:'InternalArticle', id:article.id }}
                            linkReference={{ type:'InternalArticle', id:article.id}}
                            commentReference={{ type:'InternalArticle', id:article.id }}
                        />
                    </Card>
                </Flex>

                <Divider />

                <Flex flexDirection='column' gap={16}>

                    <Flex justifyContent='space-between'>
                        <H3>Version {article.version}</H3>
                    </Flex>
                    {article.version > 1 &&
                        <Flex flexDirection='column'>
                            <Label>Ændringsbeskrivelse</Label>
                            <HtmlEditorWithAutoSave
                                id='changeDescriptionEditor'
                                value={article.changeDescription || ''}
                                ariaLabel='Beskrivelse af seneste ændringer'
                                placeholder='Beskrivelse af seneste ændringer'
                                errorMessage={changeDescriptionError}
                                onCancel={() => setChangeDescriptionError('')}
                                onSave={handleChangeDescriptionSave}
                            />
                        </Flex>
                    }

                    <Flex justifyContent='space-between' alignItems='flex-end'>
                        <FormField
                            label='Dato'
                            inputComponent={
                                <DateInput
                                    value={article.date || null}
                                    onValueChange={(date) => onChange({ field: 'date', value: date })}
                                />
                            }
                        />
                                          
                    </Flex>

                    <Flex flexDirection='column' gap={2}>
                        <Flex justifyContent='space-between' alignItems='flex-end'>
                            <Label>Evaluering</Label>
                            <ArticleImportanceSelector
                                mode='internal-evaluation'
                                selectedImportance={article.evaluation.importance || null}
                                onChangeImportance={(importance) => onChange({ field: 'evaluationImportance', value: importance })}
                            />  
                        </Flex>
                        <HtmlEditorWithAutoSave
                            id='evaluationTextEditor'
                            value={article.evaluation.text || ''}
                            ariaLabel='Evaluering af forpligtelsen eller kravet'
                            placeholder='Evaluering af forpligtelsen eller kravet...'
                            errorMessage={evaluationError}
                            onCancel={() => setEvaluationError('')}
                            onSave={handleEvaluationSave}
                        />
                    </Flex>

                    <Flex justifyContent='flex-end'>
                        <Small>Sidst redigeret: {toDateTimeString(article.lastVersionEdited.date)} af {article?.lastVersionEdited.displayName}</Small>
                    </Flex>

                </Flex>
            </Flex>


        </Card>
    );
}

type PreviousVersionCardProps = {
    change: InternalSubscriberArticleChangeView;
};
function PreviousVersionCard (props: PreviousVersionCardProps): JSX.Element
{
    return (
        <Card variant="outlined">
            <Flex flexDirection='column' gap={16}>
                <Flex alignItems='center' gap={8}>
                    <ImportanceAvatar importance={props.change.evaluation.importance || undefined} mode='internal-evaluation' />
                    <H3>Version {props.change.version}</H3>
                </Flex>

                <Flex flexDirection='column'>
                    <Label>Ændringsbeskrivelse</Label>
                    <HtmlEditorViewer small content={props.change.changeDescription} />
                </Flex>

                <Flex flexDirection='column'>
                    <Label>Dato</Label>
                    <Small>{props.change.date ? toDateString(props.change.date) : '-'}</Small>
                </Flex>

                <Flex flexDirection='column'>
                    <Label>Evaluering</Label>
                    <HtmlEditorViewer small content={props.change.evaluation.text} />
                </Flex>

                <Small>Sidst redigeret: {toDateString(props.change.lastEdited.date)} af {props.change.lastEdited.displayName}</Small>
            </Flex>
        </Card>
    );
}